// Stations on the gateway: the hello, the state reply, status reports, and the two acks
// (spec/09-realtime-protocol.md §5, §6.1, §6.2; spec/10 §6; INFRA-18, #379).
//
// ---------------------------------------------------------------------------------------------
// Which station events carry `seq`
// ---------------------------------------------------------------------------------------------
//
// spec/09 §5.2: "each event carries a per-station `seq` that increases by one and survives
// restarts (stored in the station's SQLite queue)", and the server records the last one it
// processed. That is a Postgres write per event, which is exactly right for an event worth keeping
// and exactly wrong for a keepalive (spec/19 §7 rule 1). So the events split in two:
//
//   live        `station.hello`, `station.status`, `station.netstats`, `station.webrtc.signal`.
//               Sent on the socket and never queued; they carry **no** `seq`. A status from an hour
//               ago is worthless after a reconnect — the hello that follows it re-announces the
//               station — and the gateway handles all four itself: state sync, presence in Valkey,
//               fan-out. Nothing about them reaches Postgres.
//   sequenced   every other §6.1 event. Queued in `station.db`, sent with `seq`, forwarded to the api
//               in micro-batches, processed once, acknowledged cumulatively with `station.events.ack`.
//
// A live event that arrives with a `seq`, or a sequenced one without, is refused with `err`
// `invalid-frame`: guessing which half it belongs to is how a heartbeat becomes a row.
import { z } from "../zod.js";
import { DeviceIdSchema, PlayerIdSchema, SessionIdSchema } from "../ids/schema.js";
import { OFFLINE_CODE_ACTIONS, OFFLINE_CODE_MINUTE_BUCKETS, OFFLINE_CODE_PATTERN } from "../primitives/offline-code.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
/**
 * The station's mode (spec/10 §6.1). `setting_up` (2026-10-11, ADR-0055, F-STN-26) is a start-up phase: the PC installs the
 * apps the venue marked "Install for players" that it is missing before it opens for play (`stations.setupBeforePlay`,
 * off by default). Appended, so every earlier mode keeps its place in the generated station list.
 */
export const STATION_MODES = [
    'pairing',
    'locked',
    'in_session',
    'session_locked',
    'maintenance',
    'updating',
    'disabled',
    'offline_locked',
    'setting_up',
];
export const StationModeSchema = z.enum(STATION_MODES).openapi('StationMode', {
    description: 'What the station is doing (spec/10 §6.1).',
});
// ---------------------------------------------------------------------------------------------
// The event types (spec/09 §6.1)
// ---------------------------------------------------------------------------------------------
export const STATION_HELLO = 'station.hello';
export const STATION_STATUS = 'station.status';
export const STATION_NETSTATS = 'station.netstats';
export const STATION_WEBRTC_SIGNAL = 'station.webrtc.signal';
/**
 * An offline code was used on the station (F-SES-24, spec/05 §8.4, §8.5). Reported on reconnect so the
 * cloud can settle the offline play and refuse a replay of the code. **Sequenced** — it carries a `seq`
 * and is queued (never a live keepalive, never a bulk report), so it is processed exactly once.
 *
 * `offlinecode`, one lowercase segment: a message type's segments are `[a-z][a-z0-9]*`
 * (`REALTIME_MESSAGE_TYPE_PATTERN`). It was `station.session.offline_code.used` until 2026-10-01, which the
 * envelope itself refuses — so the gateway answered every one `err invalid-frame` and none ever reached
 * the api (found by the station simulator, INFRA-14). No station build ever sent the old name: the C#
 * station has only the payload DTO, and the api accepts only this one.
 */
export const STATION_SESSION_OFFLINE_CODE_USED = 'station.session.offlinecode.used';
/** Every station → server event type in spec/09 §6.1. */
export const STATION_EVENT_TYPES = [
    STATION_HELLO,
    STATION_STATUS,
    'station.session.locked',
    'station.session.unlocked',
    'station.session.offline',
    STATION_SESSION_OFFLINE_CODE_USED,
    'station.session.ended',
    'station.game.started',
    'station.game.stopped',
    'station.apps.usage',
    'station.install.report',
    'station.title.update.progress',
    'station.hardware.report',
    STATION_NETSTATS,
    'station.alert',
    'station.client.update.status',
    STATION_WEBRTC_SIGNAL,
    'station.call.cancelled',
];
/** Sent live, never queued, never with `seq`; handled by the gateway (see the header). */
export const STATION_LIVE_EVENTS = [STATION_HELLO, STATION_STATUS, STATION_NETSTATS, STATION_WEBRTC_SIGNAL];
/**
 * Sequenced, and bulk: "app usage, install reports, hardware" go through the queue rather than the
 * realtime path (spec/09 §5.2). The api records them in the same transaction that advances the
 * station's `seq`, under their own outbox topic, so the worker processes them off the realtime path
 * and a replay cannot enqueue them twice.
 */
export const STATION_BULK_EVENTS = ['station.apps.usage', 'station.install.report', 'station.hardware.report'];
export function isStationEventType(type) {
    return STATION_EVENT_TYPES.includes(type);
}
export function isLiveStationEvent(type) {
    return STATION_LIVE_EVENTS.includes(type);
}
export function isBulkStationEvent(type) {
    return STATION_BULK_EVENTS.includes(type);
}
// ---------------------------------------------------------------------------------------------
// `station.hello` (spec/09 §5.3, §6.1)
// ---------------------------------------------------------------------------------------------
//
// Nested objects carry a JSON Schema `id` (`.meta`), so the station's generator gets a named record
// for each rather than an anonymous object it cannot name (tools/src/codegen/realtime.ts).
export const StationHelloOsSchema = z
    .object({
    /** The Windows build, e.g. `26100.4061`. */
    build: z.string().min(1).max(64),
    /** `Pro`, `Enterprise`, `IoT Enterprise LTSC`… */
    edition: z.string().min(1).max(64),
})
    .meta({ id: 'StationHelloOs' });
/** The session the station believes is running, from its own clock (spec/05 §8). */
export const StationHelloSessionSchema = z
    .object({
    id: SessionIdSchema,
    /** The `seq` of the lease it holds. */
    leaseSeq: z.int().min(0),
    elapsedSeconds: z.int().min(0),
})
    .meta({ id: 'StationHelloSession' });
export const StationCapabilitiesSchema = z
    .object({
    webrtc: z.boolean(),
    steamcmd: z.boolean(),
    desk: z.boolean(),
})
    .meta({ id: 'StationCapabilities' });
export const StationHelloPayloadSchema = z
    .object({
    /** The paired device (spec/07 §5). Absent until device identity exists (F-STN-05). */
    deviceId: DeviceIdSchema.optional(),
    clientVersion: z.string().min(1).max(64),
    uiVersion: z.string().min(1).max(64),
    os: StationHelloOsSchema,
    mode: StationModeSchema,
    /** `null` when the station believes nothing is running. */
    session: StationHelloSessionSchema.nullable(),
    /** The highest `seq` the cloud has acknowledged, as the station remembers it. */
    lastSeqAcked: z.int().min(0),
    /** Events waiting in the station's queue. */
    queueLength: z.int().min(0),
    capabilities: StationCapabilitiesSchema,
    bootedAt: IsoDateTimeSchema,
})
    .openapi('StationHelloPayload', {
    description: 'The first frame after `auth`: who the station is and what it believes (spec/09 §5.3).',
});
// ---------------------------------------------------------------------------------------------
// `station.status` (spec/09 §6.1) — live, presence only
// ---------------------------------------------------------------------------------------------
/**
 * What the station is running now (F-CAT-04, spec/10 §7.3): the library title being **played** — `titleId` set, the
 * one the station's play tracking has open (process time, so a minimised game still counts) — or, when no title is
 * being played, the program in the foreground (`titleId` null: "Other: chrome"). `null` where it is carried means
 * nothing is running worth naming (the lock screen, the kiosk, an empty desktop).
 *
 * `name` and `since` joined 2026-10-01 (optional, so an older station's frame still parses): the title's name as the
 * library offers it, and when the play (or the foreground program) started — the console's "Playing Dota 2 · 23 min".
 */
export const StationForegroundSchema = z
    .object({
    /** The catalogue title, when the process is one. */
    titleId: z.string().min(1).max(64).nullable(),
    /** The process, lower case, without `.exe` (`dota2`, `chrome`). */
    exe: z.string().min(1).max(260),
    /** The title's name (`Dota 2`) when it is a library title; absent for another program. */
    name: z.string().min(1).max(200).optional(),
    /** When this play (or this foreground program) started. */
    since: IsoDateTimeSchema.optional(),
})
    .meta({ id: 'StationForeground' });
/**
 * One alert open on the station as `station.status` reports it (F-STN-11, 2026-10-02): the station's own view, so the api can
 * converge on it — close a row the station no longer has open (a lost or forgotten clear), and re-open one staff cleared
 * by hand while the condition is still there (no silent suppression). `kind` is a plain string, not the enum, so a newer
 * station's kind never makes the whole status refuse to parse; the api ignores a kind it does not know.
 */
export const StationOpenAlertSchema = z
    .object({
    kind: z.string().min(1).max(64),
    /**
     * The last time the station saw the condition: the time of the status for a condition it still sees (disk low, the
     * guard down), the last occurrence for an event-like kind (a tamper attempt, a clock jump, the agent restarting).
     */
    lastAt: IsoDateTimeSchema,
    /** What the station saw, as on the alert. Absent when there is nothing to add. */
    detail: z.string().max(500).optional(),
})
    .meta({ id: 'StationOpenAlert' });
/** The most open alerts one `station.status` carries — more than the station has kinds. */
export const MAX_STATUS_OPEN_ALERTS = 32;
/** The most apps one start-up setup installs (ADR-0055): far more than a venue marks "Install for players". */
export const MAX_SETUP_APPS = 100;
/**
 * Where a station's start-up setup is (ADR-0055, F-STN-26): the station is `setting_up` — installing, before it opens
 * for play, the apps the venue marked "Install for players" that this PC is missing. Carried on `station.status` while
 * the mode is `setting_up` (and `null` otherwise), kept in presence, and shown on the floor tile: "Setting up · 2 of 3".
 * Presence only, like the rest of the status: never a row.
 */
export const StationSetupProgressSchema = z
    .object({
    /** The apps this setup installs, in the library's order. */
    total: z.int().min(1).max(MAX_SETUP_APPS),
    /** How many of them are done: installed, or skipped because their install failed. */
    done: z.int().min(0).max(MAX_SETUP_APPS),
    /** How many of the done were skipped (their install failed; they install later at an idle moment). */
    skipped: z.int().min(0).max(MAX_SETUP_APPS),
    /** The app installing now, as the library names it; `null` between two apps. */
    current: z.string().min(1).max(200).nullable(),
    /** When this setup began (a restart in the middle keeps it). */
    startedAt: IsoDateTimeSchema,
    /** When its time limit (`stations.setupMaxMinutes`) opens the PC for play whatever is left. */
    endsBy: IsoDateTimeSchema,
})
    .meta({ id: 'StationSetupProgress' });
export const StationStatusPayloadSchema = z
    .object({
    mode: StationModeSchema,
    /** `null` on the attract screen, the lock screen, or an empty desktop. */
    foreground: StationForegroundSchema.nullable(),
    idleSeconds: z.int().min(0),
    uptimeSeconds: z.int().min(0),
    cpuPct: z.int().min(0).max(100),
    ramPct: z.int().min(0).max(100),
    /** `null` where the GPU does not report it. */
    gpuTempC: z.int().min(-50).max(150).nullable(),
    /** Gigabytes free on the system drive, whole. */
    diskFreeGb: z.int().min(0),
    /**
     * The policy bundle version the station is enforcing (spec/10 §6.2), or `null` while it holds none. Optional:
     * a station that predates it omits it. The console compares it with the version the api would build now
     * ("Settings up to date / pending", spec/13 §3.3).
     */
    policyVersion: z.string().min(1).max(64).nullable().optional(),
    /**
     * Every alert open on the station now (F-STN-11, 2026-10-02), so the api converges on the station's own view (see
     * `StationOpenAlert`). Optional: a station that predates it omits it, and the api then changes nothing from a status.
     * An empty list means nothing is open.
     */
    openAlerts: z.array(StationOpenAlertSchema).max(MAX_STATUS_OPEN_ALERTS).optional(),
    /**
     * The start-up setup's progress while the mode is `setting_up` (ADR-0055, F-STN-26), `null` in any other mode.
     * Optional: a station that predates it omits it, and the floor then shows "Setting up" with no count.
     */
    setup: StationSetupProgressSchema.nullable().optional(),
})
    .openapi('StationStatusPayload', {
    description: 'On a change of mode or foreground title, and at least every 5 minutes. Presence only (spec/09 §8).',
});
/** `station.netstats` (spec/09 §6.1): every second, only while a member watches `station:{id}:netstats`. */
export const StationNetstatsPayloadSchema = z
    .object({
    rxBps: z.int().min(0),
    txBps: z.int().min(0),
    pingMs: z.int().min(0),
})
    .openapi('StationNetstatsPayload', { description: 'Network throughput and the ping to the gateway.' });
// ---------------------------------------------------------------------------------------------
// `station.state` (spec/09 §5.3, §6.2) — the answer to `station.hello`
// ---------------------------------------------------------------------------------------------
export const STATION_STATE = 'station.state';
export const StationStatePayloadSchema = z
    .object({
    /**
     * The authoritative session with its latest lease, or `null` — "no session". Its shape is the
     * sessions module's (F-SES-01); until sessions exist it is always `null`.
     */
    session: z.looseObject({}).nullable(),
    /** The latest signed lease for that session, or `null` (spec/05 §7). */
    lease: z.looseObject({}).nullable(),
    /** The mode the cloud holds for this station, or `null` when it holds none: keep your own. */
    mode: StationModeSchema.nullable(),
    /** The policy bundle version to have (spec/10 §6.2), or `null` while none is published. */
    policyVersion: z.string().min(1).max(64).nullable(),
    /** The library version to have, or `null` while none is published. */
    libraryVersion: z.string().min(1).max(64).nullable(),
})
    .openapi('StationStatePayload', {
    description: 'What the cloud says is true, after `station.hello`. The station reconciles both ways; pending ' +
        'non-session commands follow as their own `cmd` frames (spec/09 §5.3).',
});
// ---------------------------------------------------------------------------------------------
// The two acks
// ---------------------------------------------------------------------------------------------
/** `ack`, server → station: everything up to `upTo` is durably processed (spec/09 §5.2). */
export const STATION_EVENTS_ACK = 'station.events.ack';
export const StationEventsAckPayloadSchema = z
    .object({ upTo: z.int().min(0) })
    .openapi('StationEventsAckPayload', {
    description: 'Every event up to and including `upTo` is durably processed; delete them from the queue.',
});
export const StationCommandErrorSchema = z
    .object({
    code: z.string().min(1).max(64),
    message: z.string().max(500).optional(),
})
    .meta({ id: 'StationCommandError' });
/**
 * `ack`, station → server, with `re` = the command's id and `type` = the command's type
 * (spec/09 §5.1): `{ ok: true, result }` or `{ ok: false, error }`.
 */
export const StationCommandAckPayloadSchema = z
    .object({
    ok: z.boolean(),
    /** What the command produced, when it produces something (a directory listing, a job id). */
    result: z.looseObject({}).optional(),
    error: StationCommandErrorSchema.optional(),
})
    .refine((ack) => ack.ok || ack.error !== undefined, { message: 'A failed command says why: `error` is required.' })
    .openapi('StationCommandAckPayload', {
    description: 'The station did the command, or could not (spec/09 §5.1).',
});
// ---------------------------------------------------------------------------------------------
// `station.session.offlinecode.used` (spec/05 §8.4, §8.5; F-SES-24) — sequenced, station → server
// ---------------------------------------------------------------------------------------------
/** The action an offline code authorised (spec/05 §8.4). Mirrors `@clavian/domain`'s `OFFLINE_CODE_ACTIONS`. */
export const OfflineCodeActionSchema = z.enum(OFFLINE_CODE_ACTIONS).openapi('OfflineCodeAction', {
    description: 'What an offline code authorises: start a session, extend the running one, or unlock for maintenance (spec/05 §8.4).',
});
/**
 * The minutes an offline code carries — one of the fixed buckets (spec/05 §8.4). Emitted as a plain
 * `integer` (the C# station codegen represents no numeric enum; the value class is a station constant
 * on that side), with the fixed set enforced at runtime by the refine — so a request or a station report
 * that names an off-list bucket is refused, not coerced.
 */
export const OfflineCodeMinutesBucketSchema = z
    .int()
    .refine((minutes) => OFFLINE_CODE_MINUTE_BUCKETS.includes(minutes), {
    message: `Choose one of ${OFFLINE_CODE_MINUTE_BUCKETS.join(', ')} minutes.`,
})
    .openapi('OfflineCodeMinutesBucket', {
    description: 'Minutes of play (start, extend) or of unlocked maintenance a code carries — one of 15, 30, 60, 90, 120, 180, 240 (spec/05 §8.4).',
    example: 60,
});
export const StationSessionOfflineCodeUsedPayloadSchema = z
    .object({
    /** The 8-digit code the station accepted. The api hashes it to record the use — a code is never stored raw. */
    code: z.string().regex(OFFLINE_CODE_PATTERN),
    action: OfflineCodeActionSchema,
    minutesBucket: OfflineCodeMinutesBucketSchema,
    /** When the station accepted the code, on its own clock (spec/05 §8.3). */
    usedAt: IsoDateTimeSchema,
    /** Seconds played offline under this code, from the station's monotonic clock (spec/05 §8.3, §8.5). */
    elapsedSeconds: z.int().min(0),
    /** The session the code applied to when the station knows it (`extend`, or a `start` the cloud saw); null otherwise. */
    sessionId: SessionIdSchema.nullable(),
    /** The player signed in offline, when there is one; null for a staff-authorised start with no player yet. */
    playerId: PlayerIdSchema.nullable(),
})
    .openapi('StationSessionOfflineCodeUsedPayload', {
    description: 'A station reports, on reconnect, an offline code it accepted while disconnected (spec/05 §8.4, §8.5).',
});
// ---------------------------------------------------------------------------------------------
// `station.apps.usage` (spec/09 §6.1; F-STN-08, F-LCK-07) — sequenced + bulk, station → server
// ---------------------------------------------------------------------------------------------
/** One app's usage across a session (spec/09 §6.1): foreground time and launch count for one executable. */
export const StationAppUsageSchema = z
    .object({
    /** The executable, as the station's process monitor saw it (spec/10 §6.3). */
    exe: z.string().min(1).max(260),
    /** The catalogue title when the process is a launched title (F-CAT-04); `null` for an app that is not one. */
    titleId: z.string().min(1).max(64).nullable(),
    /** Seconds this app held the foreground during the session (spec/10 §5.1 foreground-window events). */
    foregroundSeconds: z.int().min(0),
    /** How many times it was launched during the session. */
    launches: z.int().min(0),
})
    .meta({ id: 'StationAppUsage' });
/**
 * `station.apps.usage` (spec/09 §6.1; F-STN-08, spec/10 §5.7): the per-app foreground time and launch
 * counts a station reports at the end of a session. **Sequenced and bulk** (spec/09 §5.2, `STATION_BULK_EVENTS`)
 * — queued in the station's outbox and forwarded to the api in micro-batches, so a session that ended offline
 * still reports its usage on reconnect, processed exactly once.
 */
export const StationAppsUsagePayloadSchema = z
    .object({
    sessionId: SessionIdSchema,
    /** Per-app usage. Empty for a session in which nothing ran (e.g. it only sat on the lock screen). */
    apps: z.array(StationAppUsageSchema),
})
    .openapi('StationAppsUsagePayload', {
    description: 'The per-app foreground time and launch counts for a session, reported at its end (spec/09 §6.1, spec/10 §5.7).',
});
// ---------------------------------------------------------------------------------------------
// `station.alert` (spec/09 §6.1, spec/10 §6.6, spec/11 §6; F-STN-11, F-LCK-02, F-LCK-08) — sequenced
// ---------------------------------------------------------------------------------------------
//
// A station raises an alert when something is wrong and clears it when the condition is gone — the
// clear is what removes the console's health badge without a manual dismiss (F-STN-11). Both are the
// same sequenced event (queued in the station's durable event queue, sent with `seq`, processed once),
// so an alert raised offline still reaches the cloud on reconnect, in order with its clear.
//
// The station opens a kind at most once until it clears it: "four agent restarts in ten minutes raise
// `agent_restarted` once, not four times" is the station's de-duplication, and the api's open-alert
// index (one open row per station and kind) is the second line.
export const STATION_ALERT = 'station.alert';
/**
 * The alert kinds (spec/09 §6.1). The station raises every kind but the last two; `offline_in_session` is
 * raised by the api itself when a station drops its socket while a session is running on it (N-18), and
 * `printer_offline` by the api on the desk device whose printer failed or did not answer a print job in time —
 * the job falls back and staff are told (spec/13 §11, F-OPS-08, F-FNB-05; 2026-10-01).
 */
export const STATION_ALERT_KINDS = [
    'tamper_attempt',
    'agent_restarted',
    'policy_apply_failed',
    'hook_unhealthy',
    'guard_down',
    'kiosk_not_topmost',
    'explorer_unexpected',
    'disk_low',
    'temp_high',
    'webview_missing',
    'webview_crashed',
    'clock_jump',
    'update_rolled_back',
    'offline_in_session',
    'printer_offline',
    // ADR-0051 (F-KIO-20, 2026-10-08): neither Microsoft Edge nor Google Chrome is on the PC, so the kiosk cannot open the
    // sign-in browser and hides its Google, Discord and Steam buttons there (signing in with a phone still works).
    'sign_in_browser_missing',
];
export const StationAlertKindSchema = z.enum(STATION_ALERT_KINDS).openapi('StationAlertKind', {
    description: 'What is wrong with a station (spec/09 §6.1, spec/10 §6.6, spec/11 §6).',
});
/** How loud an alert is. `critical`: the lock may not be holding, or a session is at risk; `warning`: the PC needs looking at. */
export const STATION_ALERT_SEVERITIES = ['critical', 'warning'];
export const StationAlertSeveritySchema = z.enum(STATION_ALERT_SEVERITIES).openapi('StationAlertSeverity', {
    description: '`critical` when the lock may not be holding or a session is at risk; `warning` when the PC needs looking at.',
});
/** Each kind's severity — one table, so the console badge and the api agree (spec/10 §6.6). */
export const STATION_ALERT_SEVERITY = {
    tamper_attempt: 'critical',
    agent_restarted: 'warning',
    policy_apply_failed: 'critical',
    hook_unhealthy: 'critical',
    guard_down: 'critical',
    kiosk_not_topmost: 'critical',
    explorer_unexpected: 'critical',
    disk_low: 'warning',
    temp_high: 'warning',
    webview_missing: 'warning',
    webview_crashed: 'warning',
    clock_jump: 'warning',
    update_rolled_back: 'warning',
    offline_in_session: 'critical',
    printer_offline: 'warning',
    sign_in_browser_missing: 'warning',
};
/**
 * How each kind clears by itself (F-STN-11, 2026-10-02):
 *
 *   condition      the station (or the api, for its own kinds) reports the condition gone — the disk has room again, the
 *                  guard is live after a policy apply read back, the station is back online, a print job printed.
 *   quiet_period   an event, not a state: a tamper attempt, the agent restarting, a clock jump, the kiosk crashing, an
 *                  update rolled back. The station clears it itself after its own quiet period (15 minutes with no
 *                  repeat; for `agent_restarted`, three or fewer restarts in ten minutes), and the api expires one still
 *                  open after `station.alertExpiryHours` (24 by default) with no repeat — so an alert from a station that
 *                  went away does not stay for ever.
 *
 * Staff may also clear any alert by hand (`stations:alerts_clear`); a condition the station still reports opens again.
 */
export const STATION_ALERT_CLEAR_RULES = ['condition', 'quiet_period'];
export const STATION_ALERT_CLEAR_RULE = {
    tamper_attempt: 'quiet_period',
    agent_restarted: 'quiet_period',
    policy_apply_failed: 'condition',
    hook_unhealthy: 'condition',
    guard_down: 'condition',
    kiosk_not_topmost: 'condition',
    explorer_unexpected: 'condition',
    disk_low: 'condition',
    temp_high: 'condition',
    webview_missing: 'condition',
    webview_crashed: 'quiet_period',
    clock_jump: 'quiet_period',
    update_rolled_back: 'quiet_period',
    offline_in_session: 'condition',
    printer_offline: 'condition',
    sign_in_browser_missing: 'condition',
};
/** The kinds the api raises and clears itself; a station's `openAlerts` never speaks for them. */
export const API_RAISED_STATION_ALERT_KINDS = ['offline_in_session', 'printer_offline'];
export function isStationAlertKind(value) {
    return STATION_ALERT_KINDS.includes(value);
}
/** `open` raises the kind; `cleared` says the condition is gone. */
export const STATION_ALERT_STATES = ['open', 'cleared'];
export const StationAlertPayloadSchema = z
    .object({
    kind: StationAlertKindSchema,
    state: z.enum(STATION_ALERT_STATES),
    /** What the station saw — "12 GB free on C:", "HKLM\…\NoRun reads 0" — for staff; never a secret. `null` when there is nothing to add. */
    detail: z.string().max(500).nullable(),
    /** When the station saw it open or clear, on its own clock. */
    at: IsoDateTimeSchema,
})
    .openapi('StationAlertPayload', {
    description: 'A station raises or clears a health or security alert (spec/09 §6.1, spec/10 §6.6). Sequenced.',
});
//# sourceMappingURL=station.js.map